<script lang="ts">
  import { makeApiRequest } from "$lib/utils/api";

  import * as yup from "yup";
  import { Form } from "$ui/form";
  import { Input } from "$ui/input";
  import { LoadingSpinner } from "$ui/loading-spinner";

  import { cn } from "$utils/ui";
  import CheckCircleIcon from "$assets/icons/check-circle.svelte";
  import Field from "$lib/ui/field/field.svelte";
  import { environment } from "$lib/utils/env";

  const debounce = (func: { (orgName: any): Promise<void>; (arg0: any): void }, delay: number | undefined) => {
    let timeout: string | number | NodeJS.Timeout | undefined;
    return (...args: any) => {
      clearTimeout(timeout);
      // @ts-ignore
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  const initialValues = {
    alias: "",
    org_id: "",
    os_index: "",
    agreed: false,
  };

  const validationSchema = yup.object().shape({
    alias: yup.string().required("Alias is required"),
    org_id: yup
      .string()
      .required("Organisation ID is required")
      .matches(/^[a-z0-9-]+$/, "Org ID must be lowercase and contain only letters, numbers, and hyphens"),
    agreed: yup.boolean().oneOf([true], "You must agree to the terms and conditions"),
  });

  const onSubmit = async (values: any) => {
    const url = new URL(window.location.href.replace("#", ""));
    const state = url.searchParams.get("state");

    await makeApiRequest(`/orgs/${values.org_id}/?state=${state}`, {
      alias: values.alias,
    });
    // TODO: org store
    const orgs = [];

    if (orgs.length === 0) {
      await makeApiRequest(`/me`, { preferences: { default_org: values.org_id } });
    }
  };

  let standaloneForm: boolean | undefined;
  let onSuccess: () => void;

  let form: any;
  let errors: any;
  let touched: any;
  let isSubmitting: any;
  let handleSubmit: () => void;

  let validateField: (field?: any) => any;
  let updateValidateField: (field?: any, value?: any) => void;

  let isGeneratingId = false;

  const debounceValidation = debounce(async (orgName) => {
    if (orgName) {
      isGeneratingId = true;

      errors.set({ ...$errors, tempFormError: "generating" });

      const response = await makeApiRequest(`/generate-organisation-id?alias=${orgName}`, {}, "POST");
      const generatedOrgId = response.id;

      updateValidateField("org_id", generatedOrgId);
      isGeneratingId = false;

      const resetErrors = { ...$errors };
      delete resetErrors.tempFormError;
      errors.set(resetErrors);
    } else {
      updateValidateField && updateValidateField("org_id", "");
    }
  }, 500);

  export { form, errors, touched, isSubmitting, handleSubmit, standaloneForm, onSuccess };
</script>

<div class="flex-1 flex flex-col items-stretch">
  <Form
    {initialValues}
    {validationSchema}
    {onSubmit}
    {onSuccess}
    {standaloneForm}
    bind:form
    bind:handleSubmit
    bind:validateField
    bind:updateValidateField
    bind:isSubmitting
    bind:errors
    bind:touched
  >
    {#if $form}
      <div class="relative">
        <Input
          name="alias"
          label="Organisation Name"
          placeholder="Organisation Name"
          autocomplete="off"
          error={$errors?.alias}
          bind:value={$form.alias}
          on:input={() => {
            validateField("alias");
            debounceValidation($form.alias);
          }}
        />
      </div>

      <div class="relative">
        <Input
          name="org_id"
          label="Organisation ID"
          placeholder="Organisation ID"
          error={$errors?.org_id}
          bind:value={$form.org_id}
          on:input={() => {
            validateField("org_id");
          }}
        />
        <div class="absolute top-7 right-0 h-10 px-3 flex items-center justify-center">
          {#if isGeneratingId}
            <div class="[&>svg]:w-5">
              <LoadingSpinner />
            </div>
          {:else}
            <div
              class={cn("[&>svg]:w-5", {
                "text-success-400": $form.org_id,
                "text-gray-300": !$form.org_id,
              })}
            >
              <CheckCircleIcon />
            </div>
          {/if}
        </div>
      </div>

      <Field label="" error={$errors.agreed}>
        <label class="mt-4 flex items-center gap-2 cursor-pointer" for="agreed">
          <input
            type="checkbox"
            id="agreed"
            name="agreed"
            bind:checked={$form.agreed}
            on:change={() => validateField("agreed")}
          />
          {#await environment}
            <p class="-mt-0.5">loading...</p>
          {:then env}
            <p class="-mt-0.5">
              I agree to
              <a
                href={env.legals.termsOfService}
                target="_blank"
                rel="noreferrer"
                class="text-primary dark:text-primary-700 underline text-sm">Terms of Service</a
              >
              and
              <a
                href={env.legals.privacyPolicy}
                target="_blank"
                rel="noreferrer"
                class="text-primary dark:text-primary-700 underline text-sm">Privacy Policy</a
              >
            </p>
          {/await}
        </label>
      </Field>
    {/if}
  </Form>
</div>
